import { signInWithCustomToken, signOut } from "firebase/auth";
export const SSO_ORIGIN = "https://thedreaminc.org";
const origins = [SSO_ORIGIN, "https://meet.thedreaminc.org", "https://translate.thedreaminc.org"];
export class SsoError extends Error {
    constructor(status, code) {
        super(code);
        this.status = status;
        this.code = code;
    }
}
export function isSsoEnabled() {
    return typeof window !== "undefined" && process.env.VUE_APP_SSO_ENABLED !== "false" && origins.includes(window.location.origin);
}
export async function ssoRequest(path, body = {}, idToken, signal) {
    const response = await fetch(`${SSO_ORIGIN}/api/sso/${path}`, {
        method: "POST", credentials: "include", cache: "no-store",
        headers: { "Content-Type": "application/json", ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}) },
        body: JSON.stringify(body),
        signal: signal ? AbortSignal.any([signal, AbortSignal.timeout(10000)]) : AbortSignal.timeout(10000),
    });
    const data = await response.json();
    if (!response.ok)
        throw new SsoError(response.status, data.code || "SSO_UNAVAILABLE");
    return data;
}
export async function establishSsoSession(auth) {
    if (!isSsoEnabled() || !auth.currentUser)
        return;
    await ssoRequest("session", {}, await auth.currentUser.getIdToken());
}
const base64url = (bytes) => btoa(String.fromCharCode(...bytes)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
export async function restoreSsoSession(auth, signal) {
    const session = await ssoRequest("status", {}, undefined, signal);
    signal?.throwIfAborted();
    if (auth.currentUser?.uid === session.uid) {
        try {
            await auth.currentUser.getIdToken(true);
            return;
        }
        catch {
            signal?.throwIfAborted();
        }
    }
    if (auth.currentUser)
        await signOut(auth);
    const verifier = base64url(crypto.getRandomValues(new Uint8Array(32)));
    const challenge = base64url(new Uint8Array(await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier))));
    const { code } = await ssoRequest("authorize", { challenge }, undefined, signal);
    const result = await ssoRequest("exchange", { code, verifier }, undefined, signal);
    signal?.throwIfAborted();
    if (result.uid !== session.uid)
        throw new SsoError(409, "SSO_ACCOUNT_CHANGED");
    await signInWithCustomToken(auth, result.customToken);
}
export function beginSsoLogin() {
    const returnTo = window.location.href;
    window.location.replace(`${SSO_ORIGIN}/sso?returnTo=${encodeURIComponent(returnTo)}`);
}
export function safeSsoReturnTo(value) {
    if (typeof value !== "string")
        return `${SSO_ORIGIN}/profile`;
    try {
        const url = new URL(value);
        if (!origins.includes(url.origin) || url.username || url.password || /^\/sso(?:\/|$)/.test(url.pathname))
            return `${SSO_ORIGIN}/profile`;
        return url.href;
    }
    catch {
        return `${SSO_ORIGIN}/profile`;
    }
}
export async function sharedSignOut(auth) {
    if (isSsoEnabled() && auth.currentUser) {
        await ssoRequest("logout", {}, await auth.currentUser.getIdToken());
    }
    await signOut(auth);
    if (isSsoEnabled() && window.location.origin !== SSO_ORIGIN)
        window.location.replace(SSO_ORIGIN);
}
/** Check on entry, every minute and whenever a tab becomes visible. */
export function watchSso(auth, onReady, onError, onSuccess = () => { }) {
    const controller = new AbortController();
    let busy = false;
    const check = async () => {
        if (busy || controller.signal.aborted || document.visibilityState === "hidden")
            return;
        busy = true;
        let redirecting = false;
        try {
            await auth.authStateReady();
            await restoreSsoSession(auth, controller.signal);
            if (!controller.signal.aborted)
                onSuccess();
        }
        catch (error) {
            if (controller.signal.aborted)
                return;
            if (error instanceof SsoError && (error.status === 401 || error.status === 403)) {
                try {
                    await signOut(auth);
                    if (!controller.signal.aborted) {
                        redirecting = true;
                        beginSsoLogin();
                    }
                }
                catch (signOutError) {
                    if (!controller.signal.aborted)
                        onError(signOutError);
                }
            }
            else
                onError(error);
        }
        finally {
            busy = false;
            if (!controller.signal.aborted && !redirecting)
                onReady();
        }
    };
    const onVisibility = () => { if (document.visibilityState === "visible")
        void check(); };
    document.addEventListener("visibilitychange", onVisibility);
    const timer = setInterval(() => { void check(); }, 60000);
    void check();
    return () => { controller.abort(); clearInterval(timer); document.removeEventListener("visibilitychange", onVisibility); };
}
